import { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { FormErrorMessage } from "../../components";
import { formatUSPhoneNumber, isValidUSPhoneNumber, normalizeUSPhoneNumberToE164 } from "../../utils/phoneNumberFormatting";
import { useFunctionalityAuthorizeByPhone, useFunctionalityCompanyNames, useFunctionalityLanguageByCompanyId } from "../../pages/hooks";
import { SelectModel, VrPhoneAssignmentResponse } from "../../models";
import Select from "react-select";
import { UtilsJQuery } from "../../utils";
import { PlatformType } from "../../configs";

type FormValues = {
  companyId: string;
  userId: string;
  phoneNumber: string;
  languageId: string | null;
};

type Props = {
  show: boolean;
  handleShow: (value: boolean) => void;
  onSave?: (form: FormValues) => void;
  editAssignment?: VrPhoneAssignmentResponse | null;
};

const noLanguageOption: SelectModel = {
  value: "",
  label: "No language assignment",
};

const loadingLanguageOption: SelectModel = {
  value: "__loading__",
  label: "Loading...",
};

export function AuthorizeByPhoneModal(props: Props): JSX.Element {
  const [companyNames, setCompanyNames] = useState<SelectModel[]>();
  const [selectedCompany, setSelectedCompany] = useState<SelectModel | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<SelectModel | null>(null);
  const [userOptions, setUserOptions] = useState<SelectModel[]>([]);
  const [hasLoadedUsers, setHasLoadedUsers] = useState(false);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const isEditMode = Boolean(props.editAssignment);

  const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm<FormValues>({
    defaultValues: {
      companyId: "",
      userId: "",
      phoneNumber: "",
      languageId: "",
    },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const { onCompanyNames } = useFunctionalityCompanyNames(true);
  const {
    onGetTinnedUsersByCompany,
    onUpsertPhoneAssignment,
    onCheckPhoneAssignmentExists,
  } = useFunctionalityAuthorizeByPhone();

  const phoneNumberValue = watch("phoneNumber");
  const selectedCompanyId = watch("companyId");
  const { languageList, isLoading: isLanguageLoading } = useFunctionalityLanguageByCompanyId({
    companyId: selectedCompanyId,
    plaformType: PlatformType.All,
  });
  const languageOptions = [
    noLanguageOption,
    ...(isLanguageLoading ? [loadingLanguageOption] : []),
    ...(languageList ?? []),
  ];
  const isUserLocked = !selectedCompanyId || isEditMode;
  const [phoneExistsMsg, setPhoneExistsMsg] = useState("");
  const phoneNumberRegister = register("phoneNumber", {
    required: true,
    validate: (value) => isValidUSPhoneNumber(value) || "Invalid US phone number",
  });

  const resetModal = () => {
    reset({
      companyId: "",
      userId: "",
      phoneNumber: "",
      languageId: "",
    });
    setSelectedCompany(null);
    setSelectedLanguage(null);
    setUserOptions([]);
    setHasLoadedUsers(false);
    setIsUsersLoading(false);
    setPhoneExistsMsg("");
  };

  const onClose = () => {
    resetModal();
    props.handleShow(false);
  };

  const checkPhoneAssignmentExists = async (rawValue: string) => {
    const normalized = normalizeUSPhoneNumberToE164(rawValue);
    if (!normalized || !isValidUSPhoneNumber(normalized)) {
      setPhoneExistsMsg("");
      return false;
    }
    try {
      const res = await onCheckPhoneAssignmentExists(
        normalized,
        props.editAssignment?.id
      );
      const exists = Boolean((res as any)?.exists ?? res?.data?.exists);
      if (exists) {
        setPhoneExistsMsg("This phone number is already assigned.");
        return true;
      }
      setPhoneExistsMsg("");
      return false;
    } catch (error) {
      console.error(error);
      setPhoneExistsMsg("");
      return false;
    }
  };

  const onSubmit = async (form: FormValues) => {
    const btnLoading = UtilsJQuery.Ladda(".btn-phone-authorization-save");
    btnLoading.start?.();
    try {
      const exists = await checkPhoneAssignmentExists(form.phoneNumber);
      if (exists) {
        return;
      }
      const res = await onUpsertPhoneAssignment({
        companyId: form.companyId,
        userId: form.userId,
        phoneNumber: form.phoneNumber,
        languageId: form.languageId || null,
        isEnabled: props.editAssignment?.isEnabled ?? true,
      });
      if (res?.isSuccess === false) {
        toast.error(res?.message || "Failed to save phone authorization");
        return;
      }
      toast.success(isEditMode ? "Phone authorization updated" : "Phone authorization created");
      resetModal();
      props.onSave?.(form);
      props.handleShow(false);
    } catch (error) {
      console.error(error);
      toast.error(isEditMode ? "Failed to update phone authorization" : "Failed to create phone authorization");
    } finally {
      btnLoading.stop?.();
    }
  };

  useEffect(() => {
    onCompanyNames("").then((res) => {
      setCompanyNames([
        {
          value: "",
          label: "Any",
        },
        ...(res?.data ?? []),
      ]);
    });
  }, [onCompanyNames]);

  const mapUserOptions = (payload: any): SelectModel[] => {
    const rawList = Array.isArray(payload) ? payload : payload?.data;
    const list = Array.isArray(rawList) ? rawList : rawList?.data;
    if (!Array.isArray(list)) {
      return [];
    }

    return list.map((item) => {
      const name = item?.fullName ?? item?.email ?? item?.name ?? "User";
      const tin = item?.tinNumber ?? "";
      const label = tin ? `${name} (${tin})` : name;
      const value = item?.userId ?? item?.id ?? "";

      return { label, value };
    });
  };

  const loadUsersForCompany = (companyId: string, selectedUserId?: string) => {
    setUserOptions([]);
    setHasLoadedUsers(false);
    setIsUsersLoading(true);
    if (isEditMode) {
      setIsUsersLoading(false);
      setHasLoadedUsers(true);
      return;
    }
    if (!companyId) {
      setIsUsersLoading(false);
      return;
    }
    onGetTinnedUsersByCompany(companyId)
      .then((res) => {
        const options = mapUserOptions(res);
        const editUserId = props.editAssignment?.userId;
        const editUserLabel =
          props.editAssignment?.userFullName ??
          props.editAssignment?.userEmail ??
          "Selected user";
        const shouldPrepend =
          props.editAssignment?.companyId === companyId &&
          editUserId &&
          !options.some((option) => option.value === editUserId);
        const nextOptions = shouldPrepend
          ? [{ value: editUserId, label: editUserLabel }, ...options]
          : options;
        setUserOptions(nextOptions);
        if (selectedUserId) {
          setValue("userId", selectedUserId, { shouldValidate: false, shouldDirty: true });
        }
        setHasLoadedUsers(true);
      })
      .catch(() => {
        setUserOptions([]);
        setHasLoadedUsers(true);
      })
      .finally(() => {
        setIsUsersLoading(false);
      });
  };

  useEffect(() => {
    if (!props.show || !props.editAssignment) {
      return;
    }
    const assignment = props.editAssignment;
    const companyId = assignment.companyId ?? "";
    const userId = assignment.userId ?? "";
    const phoneNumber = assignment.phoneNumberNormalized ?? assignment.phoneNumberRaw ?? "";
    const languageId = assignment.languageId ?? "";

    reset({
      companyId,
      userId,
      phoneNumber,
      languageId,
    });
    setSelectedCompany({
      value: companyId,
      label: assignment.companyName ?? "Company",
    });
    setUserOptions([
      {
        value: userId,
        label: assignment.userFullName ?? assignment.userEmail ?? "User",
      },
    ]);
    setSelectedLanguage(
      languageId
        ? {
            value: languageId,
            label: assignment.languageName ?? "Language",
          }
        : noLanguageOption
    );
    setHasLoadedUsers(true);
    setIsUsersLoading(false);
  }, [props.show, props.editAssignment, reset]);

  return (
    <Modal show={props.show} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{isEditMode ? "Edit Phone Authorization" : "Add Phone Authorization"}</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <div className="row">
            <div className="col-12">
              <div className="d-flex align-items-start">
                <div className="flex-grow-1">
                  <div className="form-group">
                    <label htmlFor="authorizeCompany" className="position-relative required">
                      Company
                    </label>
                    <Select
                      options={companyNames ?? []}
                      value={selectedCompany}
                      isDisabled={isEditMode}
                      components={{
                        IndicatorSeparator: () => null,
                      }}
                      placeholder="Select a company"
                      onChange={(value) => {
                        if (isEditMode) {
                          return;
                        }
                        const companyId = value?.value ?? "";
                        setValue("companyId", companyId, {
                          shouldValidate: false,
                          shouldDirty: true,
                        });
                        setValue("userId", "", {
                          shouldValidate: false,
                          shouldDirty: true,
                        });
                        setValue("languageId", "", {
                          shouldValidate: false,
                          shouldDirty: true,
                        });
                        setSelectedCompany(
                          value ? { label: value.label, value: value.value } : null
                        );
                        setSelectedLanguage(null);
                        loadUsersForCompany(companyId);
                      }}
                    />
                    <input type="hidden" {...register("companyId", { required: true })} />
                    {errors.companyId && <FormErrorMessage message="required" />}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1" />
              </div>
            </div>
            <div className="col-12">
              <div className="d-flex align-items-start">
                <div className="flex-grow-1">
                  <div className="form-group">
                    <label htmlFor="authorizeUser" className="position-relative required">
                      User
                    </label>
                    <select
                      id="authorizeUser"
                      className="form-control"
                      disabled={isUserLocked || isUsersLoading}
                      aria-disabled={isUserLocked || isUsersLoading}
                      {...register("userId", { required: true })}
                    >
                      {isUsersLoading ? (
                        <option value="" disabled>
                          Loading users...
                        </option>
                      ) : userOptions.length === 0 && hasLoadedUsers ? (
                        <option value="" disabled>
                          There are no tinned users under this company
                        </option>
                      ) : (
                        <option value="" disabled>
                          Select user
                        </option>
                      )}
                      {userOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {errors.userId && <FormErrorMessage message="required" />}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12">
              <div className="form-group">
                <label htmlFor="authorizePhone" className="position-relative required">
                  Phone Number
                </label>
                <input
                  id="authorizePhone"
                  type="text"
                  className="form-control"
                  placeholder="+1 (555) 123-4567"
                  value={phoneNumberValue}
                  {...phoneNumberRegister}
                  onBlur={(event) => {
                    phoneNumberRegister.onBlur(event);
                    const formatted = formatUSPhoneNumber(event.target.value);
                    setValue("phoneNumber", formatted, { shouldValidate: true });
                    checkPhoneAssignmentExists(formatted || event.target.value);
                  }}
                />
                {phoneExistsMsg && <FormErrorMessage message={phoneExistsMsg} />}
                {errors.phoneNumber && (
                  <FormErrorMessage
                    message={(errors.phoneNumber.message as string) || "required"}
                  />
                )}
                {isEditMode ? (
                  <div className="col-12 mt-2 pl-1">
                    <sup>
                      To assign the phone number to another user, delete the entry and create a new one.
                    </sup>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="col-12">
              <div className="form-group">
                <label htmlFor="authorizeLanguage" className="position-relative">
                  Language
                </label>
                <Select
                  inputId="authorizeLanguage"
                  options={languageOptions}
                  value={selectedLanguage}
                  isDisabled={!selectedCompanyId}
                  isOptionDisabled={(option) => option.value === loadingLanguageOption.value}
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  formatOptionLabel={(option) =>
                    option.value === loadingLanguageOption.value ? (
                      <div className="d-flex align-items-center">
                        <span
                          className="spinner-border spinner-border-sm mr-2"
                          role="status"
                          aria-hidden="true"
                          style={{ width: "0.9rem", height: "0.9rem" }}
                        />
                        <span>{option.label}</span>
                      </div>
                    ) : (
                      option.label
                    )
                  }
                  placeholder={!selectedCompanyId ? "Select a company first" : "Select a language"}
                  onChange={(value) => {
                    const languageId = value?.value ?? "";
                    setValue("languageId", languageId, {
                      shouldValidate: false,
                      shouldDirty: true,
                    });
                    setSelectedLanguage(
                      value ? { label: value.label, value: value.value } : null
                    );
                  }}
                />
                <input type="hidden" {...register("languageId")} />
                <small className="form-text text-muted">
                  Assigning a language will make it automatically authorized
                </small>
              </div>
            </div>

          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-primary text-uppercase" onClick={onClose}>
            Close
          </button>
          <button type="submit" className="btn btn-primary text-uppercase btn-phone-authorization-save">
            Save
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
